
import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import logoSvg from '../../assets/logo.svg';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (!error) {
      setIsUserMenuOpen(false);
      navigate('/');
    }
  };

  // Récupérer l'email admin depuis localStorage ou utiliser l'email par défaut
  const adminEmail = localStorage.getItem('admin_email') || 'fabio-coach-sportif@hotmail.fr';
  const isAdmin = user?.email === adminEmail;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <a href="/" className="flex items-center">
              <img src={logoSvg} alt="CoachFit Logo" className="h-10" />
            </a>
          </div>
          
          {/* Navigation principale */}
          <nav className="hidden md:flex items-center space-x-8">
            <a href="/" className={`transition-colors cursor-pointer ${
              isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white hover:text-teal-300'
            }`}>
              Accueil
            </a>
            <a href="/services" className={`transition-colors cursor-pointer ${
              isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white hover:text-teal-300'
            }`}>
              Services
            </a>
            <a href="/coachs" className={`transition-colors cursor-pointer ${
              isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white hover:text-teal-300'
            }`}>
              Coachs
            </a>
            <a href="/a-propos" className={`transition-colors cursor-pointer ${
              isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white hover:text-teal-300'
            }`}>
              À propos
            </a>
            <a href="/contact" className={`transition-colors cursor-pointer ${
              isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white hover:text-teal-300'
            }`}>
              Contact
            </a>
            {isAdmin && (
              <a 
                href="/admin" 
                className="flex items-center space-x-1 text-teal-600 hover:text-teal-700 font-semibold transition-colors cursor-pointer"
              >
                <i className="ri-admin-line"></i>
                <span>Admin</span>
              </a>
            )}
          </nav>
          
          {/* Actions utilisateur */}
          <div className="flex items-center space-x-4">
            {user ? (
              <div className="flex items-center space-x-4">
                <a 
                  href="/mes-reservations" 
                  className={`transition-colors hidden md:block cursor-pointer ${
                    isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white hover:text-teal-300'
                  }`}
                >
                  Mes réservations
                </a>
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors cursor-pointer ${
                      isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'
                    }`}
                  >
                    <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-white"></i>
                    </div>
                    <span className="font-medium">Mon compte</span>
                    <i className={`ri-arrow-down-s-line transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 border border-gray-100">
                      <a
                        href="/mes-reservations"
                        className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        Mes réservations
                      </a>
                      <a
                        href="/parametres"
                        className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <i className="ri-settings-3-line mr-2"></i>
                        Paramètres
                      </a>
                      {isAdmin && (
                        <a
                          href="/admin"
                          className="block w-full text-left px-4 py-2 text-teal-600 hover:bg-gray-50 cursor-pointer"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <i className="ri-admin-line mr-2"></i>
                          Administration
                        </a>
                      )}
                      <button
                        onClick={handleSignOut}
                        className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 cursor-pointer"
                      >
                        Se déconnecter
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <a 
                  href="/connexion" 
                  className={`transition-colors cursor-pointer ${
                    isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white hover:text-teal-300'
                  }`}
                >
                  Connexion
                </a>
                <a 
                  href="/devenir-coach" 
                  className="bg-teal-500 text-white px-4 py-2 rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Devenir coach
                </a>
              </>
            )}
          </div>
          
          <button 
            className={`md:hidden cursor-pointer ${
              isScrolled ? 'text-gray-700' : 'text-white'
            }`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <i className="ri-menu-line text-2xl"></i>
          </button>
        </div>
        
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100">
            <nav className="px-4 py-4 space-y-2">
              <a href="/" className="block text-gray-700 hover:text-teal-500 font-medium cursor-pointer py-2">Accueil</a>
              <a href="/coachs" className="block text-gray-700 hover:text-teal-500 font-medium cursor-pointer py-2">Coachs</a>
              <a href="/services" className="block text-gray-700 hover:text-teal-500 font-medium cursor-pointer py-2">Services</a>
              <a href="/a-propos" className="block text-gray-700 hover:text-teal-500 font-medium cursor-pointer py-2">À propos</a>
              <a href="/contact" className="block text-gray-700 hover:text-teal-500 font-medium cursor-pointer py-2">Contact</a>
              {isAdmin && (
                <a 
                  href="/admin" 
                  className="flex items-center space-x-2 text-teal-600 hover:text-teal-700 font-semibold cursor-pointer py-2"
                >
                  <i className="ri-admin-line"></i>
                  <span>Administration</span>
                </a>
              )}
              {user ? (
                <>
                  <a
                    href="/mes-reservations"
                    className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Mes réservations
                  </a>
                  <a
                    href="/parametres"
                    className="block px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <i className="ri-settings-3-line mr-2"></i>
                    Paramètres
                  </a>
                  {isAdmin && (
                    <a
                      href="/admin"
                      className="block px-4 py-2 text-teal-600 hover:bg-gray-50 rounded-lg cursor-pointer"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="ri-admin-line mr-2"></i>
                      Administration
                    </a>
                  )}
                  <button
                    onClick={handleSignOut}
                    className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer"
                  >
                    Se déconnecter
                  </button>
                </>
              ) : (
                <a
                  href="/connexion"
                  className="block px-4 py-2 bg-teal-600 text-white text-center rounded-lg hover:bg-teal-700 cursor-pointer whitespace-nowrap"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Connexion
                </a>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
